import { ResponseSuccess } from '@forwardflow/types'
import { apiGet, apiPatch, apiPost } from './Axios/configs'
import { IOnboardingData } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup, TFubUser } from '@forwardflow/types/onboarding'

export class OnboardingV2Service {
   /**
    * Start onboarding
    * @param email - Email
    * @returns ResponseSuccess<object>
    */
   static async startOnboarding({ email }: { email: string }) {
      const data = await apiPost<
         ResponseSuccess<{
            id: string
         }>
      >({
         apiPath: `/organizations/start-onboarding`,
         config: {
            params: {
               email: email,
            },
         },
         includeXOrg: false,
      })
      return data || undefined
   }

   /**
    * Verify Fub API key
    * @param token - Fub API key to verify
    * @returns ResponseSuccess<{ isCrmApiKeyValid: boolean, isOwner: boolean }>
    */
   static async verifyFubApiKey({ token }: { token: string }) {
      const data = await apiGet<
         ResponseSuccess<{ isCrmApiKeyValid: boolean; isOwner: boolean }>
      >({
         apiPath: `/organizations/identify-fub`,
         config: {
            params: {
               token,
            },
         },
         includeXOrg: false,
      })
      return data || undefined
   }

   /**
    * Submit Fub API key
    * @param organizationId - Organization ID
    * @param token - Fub API key
    * @returns ResponseSuccess<object>
    */
   static async submitFubApiKey({
      organizationId,
      token,
   }: {
      organizationId: string
      token: string
   }) {
      const data = await apiPost<ResponseSuccess<object>>({
         apiPath: `/organizations/submit-fub-api-key`,
         data: {
            token,
         },
         config: {
            params: {
               organizationId,
            },
         },
         includeXOrg: false,
      })
      return data || undefined
   }
   /**
    * Update onboarding state
    * @param organizationId - Organization ID
    * @param data - Data to update
    * @returns ResponseSuccess<object>
    */
   static async updateOnboardingState({
      organizationId,
      data,
   }: {
      organizationId: string
      data: Partial<IOnboardingData>
   }) {
      const response = await apiPatch<ResponseSuccess<IOnboardingData>>({
         apiPath: `/organizations/onboarding-states`,
         data,
         config: {
            params: {
               organizationId,
            },
         },
         includeXOrg: false,
      })
      return response || undefined
   }
   /**
    * Get onboarding state
    * @param organizationId - Organization ID
    * @returns ResponseSuccess<IOnboardingData>
    */
   static async getOnboardingState({
      organizationId,
   }: {
      organizationId: string
   }) {
      const response = await apiGet<ResponseSuccess<IOnboardingData>>({
         apiPath: `/organizations/onboarding-states`,
         config: {
            params: {
               organizationId,
            },
         },
         includeXOrg: false,
      })
      return response || undefined
   }

   /**
    * Subscribe to a package
    * @param paymentInfo - Payment information
    * @param billingPeriod - Billing period (annual/monthly)
    * @param tierId - Tier ID
    * @param seatsCount - Number of seats
    * @param couponCode - Optional coupon code
    * @returns ResponseSuccess<object>
    */
   static async subscribeToPackage({
      paymentInfo,
      billingPeriod,
      tierId,
      seatsCount,
      couponCode,
      newLeadBudget,
      reactivateLeadBudget,
   }: {
      paymentInfo: {
         cardId: string
         nameOnCard: string
         city: string
         postalCode: string
         country: string
         addressLineOne: string
      }
      billingPeriod: 'annual' | 'monthly'
      tierId: number
      seatsCount: number
      couponCode?: string
      newLeadBudget?: number
      reactivateLeadBudget?: number
   }) {
      const response = await apiPost<ResponseSuccess<object>>({
         apiPath: '/subscriptions/subscribe-to-package',
         data: {
            paymentInfo,
            billingPeriod,
            tierId,
            seatsCount,
            couponCode,
            newLeadBudget,
            reactivateLeadBudget,
         },
         includeXOrg: true,
      })
      return response || undefined
   }
   /**
    * Get Follow Up Boss users for an organization
    * @param organizationId - Organization ID
    * @param limit - Number of records per page
    * @param page - Page number
    * @returns ResponseSuccess<object>
    */
   static async getFubUsers({
      organizationId,
      limit = 100,
      page = 1,
   }: {
      organizationId: string
      limit?: number
      page?: number
   }) {
      const response = await apiGet<ResponseSuccess<TFubUser[]>>({
         apiPath: `/organizations/${organizationId}/fub-users`,
         config: {
            params: {
               limit,
               page,
            },
         },
         includeXOrg: false,
      })
      return response || undefined
   }

   /**
    * Get Follow Up Boss stages for an organization
    * @param organizationId - Organization ID
    * @param limit - Number of records per page
    * @param page - Page number
    * @returns ResponseSuccess<object>
    */
   static async getFubStages({
      organizationId,
      limit = undefined,
      page = undefined,
      search = '',
   }: {
      organizationId: string
      limit?: number
      page?: number
      search?: string
   }) {
      const response = await apiGet<
         ResponseSuccess<{ stageId: number; stageName: string }[]>
      >({
         apiPath: `/organizations/${organizationId}/fub-stages`,
         config: {
            params: {
               limit,
               page,
               search,
            },
         },
         includeXOrg: false,
      })
      return response || undefined
   }
   /**
    * Get people tags for an organization
    * @param params - Object containing the query parameters for the request
    * @returns ResponseSuccess<any>
    */
   static async getPeopleTags({
      orgId,
      page = 1,
      limit = 100,
      search = '',
   }: {
      orgId: string
      page?: number
      limit?: number
      search?: string
   }) {
      const response = await apiGet<ResponseSuccess<{ tagName: string }[]>>({
         apiPath: `/people-tags`,
         config: {
            params: {
               page,
               limit,
               search,
               orgId,
            },
         },
         includeXOrg: true,
      })
      return response || undefined
   }

   static async getSuggestedStageMappings({
      stages,
      tags,
   }: {
      stages: { stageId: number; stageName: string }[]
      tags: { tagId: number; tagName: string }[]
   }) {
      const response = await apiPost<ResponseSuccess<IStageMapGroup[]>>({
         apiPath: `/organizations/suggested-stage-mappings`,
         data: {
            stages,
            tags,
         },
         includeXOrg: false,
      })
      return response || undefined
   }
}
